import { createSelector } from 'reselect';

import { DirtyStates, Model, ReduxState, ValidationResults } from '../types';
import { selectDirtyState } from './dirty-states';
import { selectModel } from './models';
import { selectValidation } from './validations';

export { selectModel } from './models';
export { selectDirtyState } from './dirty-states';
export { selectValidation } from './validations';

interface Resource<FieldNames extends string> {
	model: Model<FieldNames>;
	dirtyState: DirtyStates<FieldNames>;
	validation: ValidationResults<FieldNames>;
}

// Selects the model plus all relevant data from the store
export const selectResourceForKey = (dataKey: string): ((state: ReduxState) => Resource<string>) =>
	createSelector(
		selectModel(dataKey),
		selectDirtyState(dataKey),
		selectValidation(dataKey),
		(model, dirtyState, validation) => ({
			model,
			dirtyState,
			validation,
		}),
	);

// Selects a keyed map of { model, dirtyState, validation } resources from the store taking an
// array of dataKeys
export const selectResourcesForKeys =
	(
		dataKeys: string[],
	): ((state: ReduxState) => {
		[dataKey: string]: Resource<string> | undefined;
	}) =>
	state =>
		dataKeys.reduce(
			(memo, dataKey) => {
				memo[dataKey] = selectResourceForKey(dataKey)(state);
				return memo;
			},
			{} as {
				[dataKey: string]: Resource<string> | undefined;
			},
		);
